import type {
  ActivityGranularity,
  ActivityHeatmapDay,
  ActivityHourCell,
  AuditLogEvent,
  SeatsSource
} from '@/models/IActivity'

/** timezone the activity days / hours are bucketed in (server side) */
export const ACTIVITY_TIME_ZONE = 'Europe/Berlin'

export const ACTIVITY_FOOTER_TEXT =
  'Internal only. Based on sign-ins and session refreshes (~10 min precision); days in Europe/Berlin.'

export const NO_VALUE = '—'

// --- calendar days (`YYYY-MM-DD`), computed in UTC to avoid DST drift ---------

const DAY_MS = 24 * 60 * 60 * 1000

export const parseDay = (day: string): Date => {
  const [y, m, d] = day.split('-').map(Number)

  return new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1))
}

export const toDay = (date: Date): string => date.toISOString().slice(0, 10)

export const addDays = (day: string, days: number): string =>
  toDay(new Date(parseDay(day).getTime() + days * DAY_MS))

export const diffDays = (from: string, to: string): number =>
  Math.round((parseDay(to).getTime() - parseDay(from).getTime()) / DAY_MS)

/** ISO weekday of a day, 1 = Monday ... 7 = Sunday */
export const isoWeekday = (day: string): number => {
  const weekday = parseDay(day).getUTCDay()

  return weekday === 0 ? 7 : weekday
}

export const startOfIsoWeek = (day: string): string =>
  addDays(day, 1 - isoWeekday(day))

export const isoWeekNumber = (day: string): number => {
  // the Thursday of the week decides the ISO year
  const thursday = parseDay(addDays(day, 4 - isoWeekday(day)))
  const yearStart = Date.UTC(thursday.getUTCFullYear(), 0, 1)

  return Math.ceil(((thursday.getTime() - yearStart) / DAY_MS + 1) / 7)
}

/** today as `YYYY-MM-DD` in the given timezone (default Europe/Berlin) */
export const todayInTimeZone = (
  now: Date = new Date(),
  timeZone = ACTIVITY_TIME_ZONE
): string =>
  // en-CA formats as YYYY-MM-DD
  new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(now)

/** inclusive day range covered by a series bucket */
export const getPeriodRange = (
  periodStart: string,
  granularity: ActivityGranularity
): { from: string; to: string } => {
  if (granularity === 'day') return { from: periodStart, to: periodStart }
  if (granularity === 'week')
    return { from: periodStart, to: addDays(periodStart, 6) }

  const start = parseDay(periodStart)
  const lastOfMonth = new Date(
    Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0)
  )

  return { from: periodStart, to: toDay(lastOfMonth) }
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
]

export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export const monthLabel = (day: string): string =>
  MONTHS[parseDay(day).getUTCMonth()]

/** `24.09.2026` */
export const formatDay = (day: string): string => {
  const [y, m, d] = day.split('-')

  return `${d}.${m}.${y}`
}

/** short axis label of a bucket: `24.09.`, `W39`, `Sep 26` */
export const formatPeriodShort = (
  periodStart: string,
  granularity: ActivityGranularity
): string => {
  if (granularity === 'week') return `W${isoWeekNumber(periodStart)}`
  if (granularity === 'month')
    return `${monthLabel(periodStart)} ${periodStart.slice(2, 4)}`

  const [, m, d] = periodStart.split('-')

  return `${d}.${m}.`
}

/** full label of a bucket: `24.09.2026`, `W39 (21.09.2026 – 27.09.2026)`, `Sep 2026` */
export const formatPeriodLong = (
  periodStart: string,
  granularity: ActivityGranularity
): string => {
  if (granularity === 'day') return formatDay(periodStart)
  if (granularity === 'month')
    return `${monthLabel(periodStart)} ${periodStart.slice(0, 4)}`

  const { from, to } = getPeriodRange(periodStart, granularity)

  return `W${isoWeekNumber(periodStart)} (${formatDay(from)} – ${formatDay(
    to
  )})`
}

export const GRANULARITY_LABELS: Record<ActivityGranularity, string> = {
  day: 'Days',
  week: 'Weeks',
  month: 'Months'
}

export const GRANULARITY_UNIT: Record<ActivityGranularity, string> = {
  day: 'day',
  week: 'week',
  month: 'month'
}

// --- numbers ------------------------------------------------------------------

const numberFormat = new Intl.NumberFormat('en-US')

export const formatNumber = (value?: number | null): string =>
  value === null || value === undefined || Number.isNaN(value)
    ? NO_VALUE
    : numberFormat.format(value)

/** 0.873 -> `87%` */
export const formatPercent = (
  rate?: number | null,
  fractionDigits = 0
): string =>
  rate === null || rate === undefined || Number.isNaN(rate)
    ? NO_VALUE
    : `${(rate * 100).toFixed(fractionDigits)}%`

export type Delta = {
  /** relative change in percent, rounded; undefined when there is no baseline */
  percent?: number
  direction: 'up' | 'down' | 'flat'
  /** `+6%`, `-12%`, `±0%`, or `new` without a baseline */
  label: string
}

export const computeDelta = (current: number, previous: number): Delta => {
  if (!previous) {
    return current > 0
      ? { direction: 'up', label: 'new' }
      : { percent: 0, direction: 'flat', label: '±0%' }
  }

  const percent = Math.round(((current - previous) / previous) * 100)

  if (percent === 0) return { percent, direction: 'flat', label: '±0%' }

  return {
    percent,
    direction: percent > 0 ? 'up' : 'down',
    label: `${percent > 0 ? '+' : ''}${percent}%`
  }
}

/** `below 50%` activation is flagged in the UI */
export const LOW_ACTIVATION_THRESHOLD = 0.5

export const isLowActivation = (rate?: number | null): boolean =>
  rate !== null && rate !== undefined && rate < LOW_ACTIVATION_THRESHOLD

// --- seats --------------------------------------------------------------------

type SeatFields = {
  seats?: number | null
  /** @deprecated fallback while user-api still ships only the alias */
  paid_seats?: number | null
  included_seats?: number | null
  included_seats_source?: SeatsSource | null
  billable_members?: number | null
}

/** effective seats: `seats`, or the deprecated `paid_seats` if `seats` is absent */
export const getSeats = (item?: SeatFields | null): number | null => {
  if (!item) return null
  if (item.seats !== undefined) return item.seats

  return item.paid_seats ?? null
}

export const SEATS_SOURCE_LABELS: Record<SeatsSource, string> = {
  override: 'tier override',
  tier: 'tier'
}

/** `Included 40 (tier override) · billable 45` */
export const formatSeatsHint = (item?: SeatFields | null): string => {
  if (!item) return NO_VALUE

  const included =
    item.included_seats === null || item.included_seats === undefined
      ? 'No tier quota'
      : `Included ${formatNumber(item.included_seats)}${
          item.included_seats_source
            ? ` (${SEATS_SOURCE_LABELS[item.included_seats_source]})`
            : ''
        }`

  return `${included} · billable ${formatNumber(item.billable_members)}`
}

// --- heatmap --------------------------------------------------------------------

export type HeatmapCell = {
  day: string
  value: number
  /** 0 (none) .. 4 (max) */
  level: number
  /** outside of from..to (padding to full weeks) */
  outside: boolean
}

export type HeatmapGrid = {
  /** columns = weeks (Mon..Sun rows) */
  weeks: HeatmapCell[][]
  /** month label at the first week column that starts in a new month */
  months: { index: number; label: string }[]
  max: number
}

export const HEATMAP_LEVELS = 5

/** level 0 for 0, else 1..4 relative to max (binary data -> always level 4) */
export const heatmapLevel = (value: number, max: number): number => {
  if (value <= 0 || max <= 0) return 0

  return Math.min(
    HEATMAP_LEVELS - 1,
    Math.max(1, Math.ceil((value / max) * (HEATMAP_LEVELS - 1)))
  )
}

/**
 * Fills the sparse `days` into full Monday-based weeks from `from` to `to`.
 */
export const buildHeatmapGrid = (
  days: ActivityHeatmapDay[],
  from: string,
  to: string
): HeatmapGrid => {
  const values = new Map(days.map((d) => [d.day, d.value]))
  const max = days.reduce((acc, d) => Math.max(acc, d.value), 0)
  const start = startOfIsoWeek(from)
  const total = diffDays(start, to) + 1
  const weekCount = Math.max(0, Math.ceil(total / 7))

  const weeks: HeatmapCell[][] = []
  const months: HeatmapGrid['months'] = []

  for (let w = 0; w < weekCount; w++) {
    const week: HeatmapCell[] = []

    for (let d = 0; d < 7; d++) {
      const day = addDays(start, w * 7 + d)
      const outside = day < from || day > to
      const value = outside ? 0 : values.get(day) ?? 0

      week.push({ day, value, level: heatmapLevel(value, max), outside })
    }

    // label a month at the column containing its 1st day (and the first column)
    const labelDay =
      w === 0
        ? week.find((c) => !c.outside)?.day
        : week.find((c) => !c.outside && c.day.endsWith('-01'))?.day

    if (labelDay) months.push({ index: w, label: monthLabel(labelDay) })

    weeks.push(week)
  }

  // drop a label that would collide with the next one (first partial column)
  const spacedMonths = months.filter(
    (m, i) => i === months.length - 1 || months[i + 1].index - m.index >= 2
  )

  return { weeks, months: spacedMonths, max }
}

// --- hour grid ------------------------------------------------------------------

export type HourGrid = {
  /** rows = weekday Mon..Sun, columns = hour 0..23 */
  rows: number[][]
  max: number
  peak?: { weekday: number; hour: number; value: number }
}

export const buildHourGrid = (cells: ActivityHourCell[]): HourGrid => {
  const rows = Array.from({ length: 7 }, () => Array<number>(24).fill(0))
  let peak: HourGrid['peak']

  for (const cell of cells) {
    if (cell.weekday < 1 || cell.weekday > 7 || cell.hour < 0 || cell.hour > 23)
      continue

    rows[cell.weekday - 1][cell.hour] = cell.avg_active_users

    if (!peak || cell.avg_active_users > peak.value) {
      peak = {
        weekday: cell.weekday,
        hour: cell.hour,
        value: cell.avg_active_users
      }
    }
  }

  return { rows, max: peak?.value ?? 0, peak }
}

export const formatHourRange = (hour: number): string => {
  const pad = (h: number) => String(h % 24).padStart(2, '0')

  return `${pad(hour)}:00–${pad(hour + 1)}:00`
}

// --- people -------------------------------------------------------------------

export const getInitials = (name?: string | null, email?: string | null) =>
  (name || email || '?')
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')

// --- audit log ----------------------------------------------------------------

export type AuditCategory = 'all' | 'auth' | 'changes'

const AUTH_PATTERN =
  /login|logout|sign_?in|organization_switched|switchorganization|mfa|password/i

export const getAuditCategory = (
  event: Pick<AuditLogEvent, 'event_name' | 'action'>
): Exclude<AuditCategory, 'all'> =>
  AUTH_PATTERN.test(`${event.action ?? ''} ${event.event_name ?? ''}`)
    ? 'auth'
    : 'changes'

const humanize = (value: string) => {
  const spaced = value
    .replace(/[._]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase()

  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const LOGIN_METHOD_LABEL: Record<string, string> = {
  password: 'with password',
  sso: 'via SSO',
  magic_link: 'via magic link',
  passkey: 'via passkey',
  switch_org: 'via organization switch',
  login_as: 'by epilot support'
}

const parseAuditDetail = (
  detail: AuditLogEvent['detail']
): Record<string, unknown> => {
  if (!detail) return {}
  if (typeof detail === 'object') return detail

  try {
    const parsed = JSON.parse(detail)

    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

/**
 * Sign-in / org switch events get their own titles: the API's generic `activity`
 * text for them reads "<email> a resource in 360 portal".
 */
export const getAuditTitle = (event: AuditLogEvent): string => {
  const kind = event.action || event.event_name

  if (kind === 'user.login') {
    const detail = parseAuditDetail(event.detail)
    const method = detail.impersonated ? 'login_as' : detail.login_method
    const label = typeof method === 'string' && LOGIN_METHOD_LABEL[method]

    return label ? `Signed in ${label}` : 'Signed in'
  }

  if (kind === 'user.organization_switched') {
    return parseAuditDetail(event.detail).direction === 'out'
      ? 'Switched to another organization'
      : 'Switched into this organization'
  }

  return (
    event.activity?.trim() ||
    (event.action && humanize(event.action)) ||
    (event.event_name && humanize(event.event_name)) ||
    'Event'
  )
}

/** `12:28` today, `Yesterday 16:40`, else `22.09. 15:12` (older years with year) */
export const formatAuditTime = (
  timestamp: string,
  now: Date = new Date()
): string => {
  const date = new Date(timestamp)

  if (Number.isNaN(date.getTime())) return NO_VALUE

  const time = date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  })
  const startOfToday = new Date(now)

  startOfToday.setHours(0, 0, 0, 0)

  const dayDiff = Math.floor(
    (startOfToday.getTime() - date.getTime()) / DAY_MS + 1
  )

  if (date >= startOfToday) return time
  if (dayDiff <= 1) return `Yesterday ${time}`

  const sameYear = date.getFullYear() === now.getFullYear()
  const day = date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    ...(sameYear ? {} : { year: 'numeric' })
  })

  return `${day}${sameYear ? '' : ','} ${time}`
}
